import { Component, inject, input, output, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { getTypeOptions, getSystemMonths } from '../../../shared/question-types/question-types.registry';
import { parseMonths, normalizeMonths, joinMonths, uniqueSorted } from '../../../shared/period.utils';
import { confirmRemoveSubquestionsDialog } from './question-dialogs';
import { parentEvidenceReset, parentGoalReset, parentScoreReset } from './admin-question-form-helpers';

@Component({
  selector: 'app-admin-question-form',
  templateUrl: './admin-question-form.component.html',
  standalone: true,
  imports: [ReactiveFormsModule],
})
export class AdminQuestionFormComponent implements OnInit {
  question = input<any | null>(null)
  type = input<'question' | 'subquestion'>('question')
  selectCategoryId = input<string>('')
  yearSelected = input<number>(0)
  questionSystem = input<any>(null)

  save = output<any>()
  close = output<void>()

  protected fb = inject(FormBuilder)

  protected monthsList = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

  protected form = this.fb.group({
    id: [null],
    parentId: [null],
    categoryId: [''],
    title: ['', Validators.required],
    tooltip: [null],
    year: [''],
    typeId: [''],
    score: [1],
    evidenceRequired: [false],
    evidenceTrue: [null],
    evidenceFalse: [null],
    evidenceType: [null],
    goalType: ['general'],
    goalValue: [null],
    deadline: [null],
    months: [null],
    enabled: [true],
    hasSubquestions: [false],
  })

  protected get questionTypes() {
    return getTypeOptions(this.questionSystem())
  }

  protected get hasHistory() {
    return !!this.question()?.hasHistory
  }

  protected get typeName() {
    return this.questionTypes.find(t => t.id === this.form.value.typeId)?.label ?? ''
  }

  protected get isBooleanType() {
    const typeId = this.form.value.typeId;
    const t = this.questionTypes.find(x => x.id === typeId);
    return t?.dataType === 'boolean';
  }

  protected get showEvidenceSelectors() {
    return this.isBooleanType && !!this.form.value.evidenceRequired;
  }

  protected get showSingleEvidenceSelector() {
    return !this.isBooleanType && !!this.form.value.evidenceRequired;
  }

  protected evidenceOptions = [
    { value: 'file', label: 'Archivo' },
    { value: 'text', label: 'Texto' },
    { value: 'file_or_text', label: 'Archivo o Texto' },
    { value: 'none', label: 'Ninguna' },
  ]

  protected get evaluateAllMonths() {
    return getSystemMonths(this.questionSystem()) === 'all'
  }

  protected get isAnnual() {
    return this.questionSystem()?.scoringMode === 'annual'
  }

  protected get isParentMode() {
    return this.form.value.hasSubquestions === true
  }

  protected get isMonthly() {
    return this.form.value.months === null
  }

  protected get subCount() {
    return this.countSubitems()
  }

  private countSubitems() {
    const subs = this.question()?.subitems
    if (subs) return subs.length
    return 0
  }

  ngOnInit() {
    this.patchInitialValues()
    this.initSubMode()
    this.initDefaultType()
  }

  private patchInitialValues() {
    const q = this.question()
    if (q) this.form.patchValue(q)
    this.form.patchValue({
      categoryId: this.selectCategoryId(),
      year: String(this.yearSelected()),
    })
    this.normalizeLoadedMonths()
  }

  private normalizeLoadedMonths() {
    const q: any = this.question()
    if (!q) return
    this.form.patchValue({ months: normalizeMonths(q.months) })
  }

  private initSubMode() {
    if (this.subCount > 0) this.form.patchValue({ hasSubquestions: true })
  }

  private initDefaultType() {
    this.ensureTypeSelected()
  }

  private ensureTypeSelected() {
    const types = this.questionTypes
    if (!this.form.value.typeId && types.length) this.form.patchValue({ typeId: types[0].id })
  }

  protected isMonthSelected(month: number): boolean {
    return parseMonths(this.form.value.months).includes(month)
  }

  toggleMonth(month: number) {
    const current = parseMonths(this.form.value.months)
    this.form.patchValue({ months: this.joinOrNull(current, month) })
  }

  private joinOrNull(current: number[], month: number) {
    const idx = current.indexOf(month)
    if (idx > -1) current.splice(idx, 1)
    else current.push(month)
    if (!current.length) return null
    return joinMonths(uniqueSorted(current))
  }

  onMonthlyChange(event: any) {
    if (event.target.checked) {
      this.form.patchValue({ months: null })
    } else {
      this.form.patchValue({ months: '' })
    }
  }

  onSubSwitchChange(event: any) {
    if (this.hasHistory) return
    if (event.target.checked) {
      this.ensureTypeSelected()
      this.resetParentFields()
      return
    }
    this.confirmSubRemoval(event)
  }

  private resetParentFields() {
    this.form.patchValue(parentScoreReset());
    this.form.patchValue(parentEvidenceReset());
    this.form.patchValue(parentGoalReset());
  }

  private confirmSubRemoval(event: any) {
    if (this.subCount === 0) return
    this.revertSubSwitch(event)
    this.askRemoveSubs()
  }

  private revertSubSwitch(event: any) {
    event.target.checked = true
    this.form.patchValue({ hasSubquestions: true })
  }

  private askRemoveSubs() {
    confirmRemoveSubquestionsDialog(this.subCount, () => this.disableSubMode())
  }

  private disableSubMode() {
    this.form.patchValue({ hasSubquestions: false })
  }

  onSave() {
    if (this.form.invalid) return
    this.save.emit(this.form.value)
  }
}